import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useScrollPosition } from '@hooks/useScrollPosition';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { NAV_LINKS } from '@utils/constants';
import { cn } from '@utils/helpers';
import { openDemoModal } from '@components/common/DemoModal';
import logo from '../../../../public/logo.png';
import qesLogo from '../../../../public/qeslogo.png';

function Header() {
    const { isScrolled } = useScrollPosition(60);
    const isMobile = useMediaQuery('(max-width: 1279px)');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const drawerRef = useRef(null);

    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    // Close drawer when desktop breakpoint is hit
    useEffect(() => {
        if (!isMobile) setDrawerOpen(false);
    }, [isMobile]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [drawerOpen]);

    // Close on Escape key
    useEffect(() => {
        if (!drawerOpen) return;
        const handleKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [drawerOpen, closeDrawer]);

    return (
        <>
            {/* ── HEADER BAR ─────────────────────────────────────────── */}
            <header
                className={cn(
                    'sticky top-0 z-50 transition-all duration-300',
                    isScrolled
                        ? 'bg-white/98 backdrop-blur-md shadow-sm border-b border-neutral-200'
                        : 'bg-white border-b-2 border-primary-100 shadow-sm'
                )}
                role="banner"
            >
                {/* Accent bottom line */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-accent-400/60 to-transparent pointer-events-none" aria-hidden="true" />

                <nav className="container-site flex items-center justify-between h-16 xl:h-20" aria-label="Main navigation">
                    {/* Logo */}
                    <div className="flex flex-row items-center gap-2  ">
                        <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
                            <img src={logo} alt="Company Logo" className="h-10 w-auto" />
                        </Link>
                        {/* <span className="hidden sm:inline text-[7px] leading-tight text-black whitespace-nowrap">
                            In collaboration with
                        </span>
                        <img src={qesLogo} alt="QES logo" className="h-8 mt-2 w-auto" /> */}
                    </div>

                    {/* Desktop Nav */}
                    <ul className="hidden xl:flex items-center space-x-10 font-menu uppercase" role="list">
                        {/* {NAV_LINKS.filter(l => l.href !== '/').map(({ label, href }) => (
                            <li key={href}>
                                <NavLink
                                    to={href}
                                    className={({ isActive }) =>
                                        cn(
                                            'px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200',
                                            isActive ? 'text-accent-500' : 'text-primary-800 hover:text-primary-950'
                                        )
                                    }
                                >
                                    {({ isActive }) => (
                                        <span className={cn('nav-link-underline', isActive && 'active')}>
                                            {label}
                                        </span>
                                    )}
                                </NavLink>
                            </li>
                        ))} */}

                        {NAV_LINKS.filter(l => l.href !== '/').map(({ label, href, target }) => (
                            <li key={href}>
                                {target ? (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 text-primary-800 hover:text-primary-950"
                                    >
                                        <span className="nav-link-underline">{label}</span>
                                    </a>
                                ) : (
                                    <NavLink
                                        to={href}
                                        className={({ isActive }) =>
                                            cn(
                                                'px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200',
                                                isActive ? 'text-accent-500' : 'text-primary-800 hover:text-primary-950'
                                            )
                                        }
                                    >
                                        {({ isActive }) => (
                                            <span className={cn('nav-link-underline', isActive && 'active')}>
                                                {label}
                                            </span>
                                        )}
                                    </NavLink>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* Desktop CTA */}
                    <div className="hidden xl:flex items-center gap-3 font-menu">
                        <button
                            type="button"
                            onClick={openDemoModal}
                            className="btn btn-md btn-primary text-sm font-semibold"
                        >
                            Request Demo
                        </button>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setDrawerOpen(true)}
                        className="xl:hidden p-2 rounded-lg transition-colors text-primary-700 hover:bg-neutral-100"
                        aria-label="Open navigation menu"
                        aria-expanded={drawerOpen}
                        aria-controls="mobile-drawer"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </nav>
            </header>

            {/* ── DRAWER BACKDROP ────────────────────────────────────── */}
            <div
                aria-hidden="true"
                onClick={closeDrawer}
                className={cn(
                    'fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm xl:hidden transition-opacity duration-300',
                    drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                )}
            />

            {/* ── LEFT DRAWER ────────────────────────────────────────── */}
            <aside
                ref={drawerRef}
                id="mobile-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
                className={cn(
                    'fixed top-0 left-0 h-full w-72 z-[70] xl:hidden',
                    'flex flex-col',
                    'bg-[#070d1f] border-r border-white/10',
                    'shadow-2xl',
                    'transition-transform duration-300 ease-in-out',
                    drawerOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                {/* Drawer header */}
                <div className="flex bg-white items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Link to="/" onClick={closeDrawer} className="flex items-center">
                            <img src={logo} alt="Company Logo" className="h-9 w-auto" />
                        </Link>
                        {/* <span className="hidden sm:inline text-[8px] leading-tight text-neutral-400 whitespace-nowrap">
                            In collaboration with
                        </span> */}
                        {/* <img src={qesLogo} alt="QES logo" className="h-6 mt-2 w-auto object-contain" /> */}
                    </div>
                    <button
                        onClick={closeDrawer}
                        className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Close menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Decorative accent line */}
                <div className="h-[2px] bg-gradient-to-r from-accent-500/80 via-indigo-400/60 to-transparent flex-shrink-0" />

                {/* Nav links */}
                <nav className="flex-1 overflow-y-auto px-3 py-6 font-menu" aria-label="Mobile navigation">
                    <ul className="space-y-1" role="list">
                        {NAV_LINKS.map(({ label, href }) => (
                            <li key={href}>
                                <NavLink
                                    to={href}
                                    end={href === '/'}
                                    onClick={closeDrawer}
                                    className={({ isActive }) =>
                                        cn(
                                            'group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold tracking-widest uppercase transition-all duration-200',
                                            isActive
                                                ? 'bg-accent-500/15 text-accent-400 border border-accent-500/30'
                                                : 'text-white/70 hover:bg-white/8 hover:text-white'
                                        )
                                    }
                                >
                                    {/* Active indicator bar */}
                                    {({ isActive }) => (
                                        <>
                                            <span
                                                className={cn(
                                                    'w-1 h-4 rounded-full flex-shrink-0 transition-all duration-200',
                                                    isActive ? 'bg-accent-400' : 'bg-white/0 group-hover:bg-white/30'
                                                )}
                                            />
                                            {label}
                                        </>
                                    )}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Drawer footer — CTA */}
                <div className="px-5 py-5 border-t border-white/10 flex-shrink-0 space-y-3">
                    <button
                        type="button"
                        onClick={() => { closeDrawer(); openDemoModal(); }}
                        className="w-full btn btn-md btn-primary justify-center text-sm font-semibold tracking-wide"
                    >
                        Request Demo
                    </button>
                    <p className="text-center text-white/30 text-xs tracking-wide">
                        VisionAI by Elliance
                    </p>
                </div>
            </aside>
        </>
    );
}

export default Header;
